import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { Anchor, Checkbox, Dialog, FormattedText, Toolbar } from "client/Components";
import { verifyPermissions, VerifyPermissionsResult } from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { AlertManager, useAlerts } from "client/Hooks";

export type UpdateFailDialogProps = {
	onBack: () => void;
	onClose: () => void;
	onSuccess: (consentUrl: string) => void;
	reason: string;
};

export const UpdateFailDialog = (props: UpdateFailDialogProps): ReactElement => {
	const {
		onClose,
		onSuccess,
		onBack,
		reason
	} = props;

	const [checked, setChecked] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const articleLink = "https://support.workspace365.net/hc/en-us/articles/4408392843410-Manual-setup-of-SSO";

	const handleCheckboxChange = useCallback(
		(): void => {
			setChecked(prevState => !prevState);
		},
		[]
	);

	const handleVerifyClick = useCallback(
		async (): Promise<void> => {
			try {
				setIsLoading(true);
				const result: VerifyPermissionsResult = await verifyPermissions();

				setIsLoading(false);

				if (result.permissionsAreUpToDate) {
					onSuccess(result.adminConsentUrl);
				} else {
					alertManager.showError(localize("_VerificationFailedPleaseMakeSureTheSsoAppIsUpdatedCorrectlyAndTryAgain"));
				}

			} catch (e) {
				setIsLoading(false);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[onSuccess, alertManager]
	);

	return (
		<Dialog
			trackingId="update-fail-dialog"
			hasBusyIndicator={isLoading}
		>
			<Dialog.Header
				title={localize("_UpdateFailed")}
				onClose={onClose}
				onBack={onBack}
			/>
			<Dialog.Body>
				<p>
					<strong>{localize("_Reason")}</strong>: {reason}
				</p>
				<p>
					<FormattedText
						text={localize("_ToContinueYouHaveToManuallyUpdateTheSsoApp")}
						thingsToReplace={[
							{
								what: "{{ThisArticle}}",
								filler: <Anchor href={articleLink} target="_blank">{localize("_ThisArticle")}</Anchor>
							}
						]}
					/>
				</p>
				<Checkbox
					trackingId="manual-update-confirmation"
					value={checked}
					onChange={handleCheckboxChange}
					text={localize("_IHaveManuallyUpdatedTheSsoApp")}
				/>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="back-button"
						kind="secondary"
						text={localize("_Back")}
						onClick={onBack}
						responsiveBehavior="hide"
					/>
					<Toolbar.Button
						trackingId="verify-button"
						kind="primary"
						text={localize("_Verify")}
						onClick={handleVerifyClick}
						isDisabled={!checked}
					/>
					<Toolbar.Button
						trackingId="cancel-button"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

UpdateFailDialog.displayName = "UpdateFailDialog";
